import { formatNumber } from '@shared/utils/formatNumber';
import { CargoUnitLabels } from '@shared/utils/constants';
import { splitActionKey } from '@shared/utils/splitActionKey';
import { CargoDetails, GetCargoDetailsResponse } from '@api/features/cargos/getCargoDetails';
import { CargoPortProps } from '@api/models/cargo-port';
import { CargoProps } from '@api/models/cargo';

export type CargoPortObj = { [s: string]: CargoPortProps };

export const getRemoveCargoDisabledStr = (
	cargo: CargoDetails
) => {
	if (!cargo) {
		return [];
	}

	const errorStrArr: string[] = [];

	if (cargo.hasActualDates) {
		errorStrArr.push('Cargo has port calls with actual dates');
	}

	if (cargo.hasBunkerStem) {
		errorStrArr.push('Cargo has bunker stem attached');
	}

	if (cargo.hasCargoPortAsCommencement) {
		errorStrArr.push('Cargo has port marked as commencement');
	}

	if (cargo.hasInvoices) {
		errorStrArr.push('Cargo has invoices attached');
	}

	if (cargo.hasNonEstimatedVoyageExpenses) {
		errorStrArr.push('Cargo has non-estimated expenses attached');
	}

	if (cargo.hasPaymentsOnVoyageExpenses) {
		errorStrArr.push('Cargo has expenses with payments made attached');
	}

	return errorStrArr;
};

export const getCargoDescription = (cargo: GetCargoDetailsResponse | undefined) => {
	let string = '';

	if (cargo == null || (cargo.quantity == null && cargo.type == null)) {
		return 'Unknown cargo';
	}

	if (cargo.quantity != null) {
		string += `${formatNumber(cargo.quantity, { separateThousands: true })} ${CargoUnitLabels[cargo.unit]} `;
	}

	if (cargo.type != null) {
		string += `${cargo.type} `;
	}

	if (cargo.charterer != null && cargo.chartererName != null) {
		string += `for ${cargo.chartererName}`;
	}

	return string;
};

export const getLoadedAndDischarged = (fields: CargoPortObj) => {
	const obj = Object.entries(fields);

	return obj.reduce((acc, [key, values]) => {
		const { action } = splitActionKey(key);

		if (values == null) {
			return acc;
		}

		if (action === 'loading') {
			return {
				...acc,
				loaded: acc.loaded + (values.cpQuantity ?? 0),
			};
		}

		return {
			...acc,
			discharged: acc.discharged + (values.cpQuantity ?? 0),
		};
	}, { loaded: 0, discharged: 0 });
};

export const getMissingCargoPortFields = (cargoPort: CargoPortProps) => {
	const missingOnCargoPort: string[] = [];

	const {
		cpQuantity,
		laytimeInterval,
		calculationMethod,
	} = cargoPort;

	if (!cpQuantity) {
		missingOnCargoPort.push('Amount loaded');
	}

	if (!laytimeInterval) {
		missingOnCargoPort.push('Laytime interval');
	}

	if (!calculationMethod) {
		missingOnCargoPort.push('Calculation method');
	}

	return {
		portName: cargoPort.port.name,
		missingOnCargoPort,
	};
};

export const getMissingCargoFields = (cargo: CargoProps) => {
	const missingOnCargo: string[] = [];

	const {
		quantity,
		unit,
		freightRate,
		currency,
		freightType,
		charterer,
		loadingPorts,
		dischargePorts,
		laytimeTerms,
		terms,
	} = cargo;

	if (!quantity) {
		missingOnCargo.push('Quantity');
	}

	if (!unit) {
		missingOnCargo.push('Unit');
	}

	if (!freightRate) {
		missingOnCargo.push('Freight rate');
	}

	if (!currency) {
		missingOnCargo.push('Currency');
	}

	if (!freightType) {
		missingOnCargo.push('Freight type');
	}

	if (!terms) {
		missingOnCargo.push('Terms');
	}

	if (!charterer) {
		missingOnCargo.push('Charterer');
	}

	if ((loadingPorts ?? []).length === 0) {
		missingOnCargo.push('Loading ports');
	}

	if ((dischargePorts ?? []).length === 0) {
		missingOnCargo.push('Discharge ports');
	}

	if (!laytimeTerms) {
		missingOnCargo.push('Laytime terms');
	}

	return missingOnCargo;
};